@if (loading()) {
    <app-loader></app-loader>
} @else if (form) {
    <form class="tw:w-full tw:px-6 tw:py-8 tw:pb-28" [formGroup]="form" (ngSubmit)="onSubmit()">
        <h6 class="tw:mb-6 tw:h6">{{ 'INVOICE_PAYMENT_METHOD.title' | translate }}</h6>

        <div class="tw:mb-6 tw:rounded-lg tw:bg-primary-50 tw:p-4">
            <div class="tw:mb-2 tw:flex tw:justify-between">
                <div class="tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.clientSection' | translate }}</div>
                @if (!clientEditMode() && canEditClientDetails()) {
                    <a class="tw:font-semibold tw:no-underline tw:cursor-pointer" (click)="clientEditMode.set(true)">{{
                        'GENERAL.edit' | translate
                    }}</a>
                }
            </div>
            <div class="tw:flex tw:justify-between tw:gap-2 tw:text-sm">
                @if (clientEditMode()) {
                    <div class="tw:w-1/2">
                        <app-text-input
                            [hasMarginB]="false"
                            formControlName="clientVatNumber"
                            label="{{ 'INVOICE_PAYMENT_METHOD.vat' | translate }}"
                            [submitAttempted]="submitAttempted()"
                            [invalidMsg]="clientVatInvalidMessage()"
                        />
                    </div>
                    <div class="tw:w-1/2">
                        <app-text-input [hasMarginB]="false" formControlName="clientName" label="{{ 'INVOICE_PAYMENT_METHOD.name' | translate }}" />
                    </div>
                } @else {
                    <div class="tw:w-1/2">
                        <div class="tw:font-semibold">{{ 'INVOICE_PAYMENT_METHOD.vat' | translate }}</div>
                        <div class="tw:text-neutral-500">{{ form.value.clientVatNumber || '-' }}</div>
                        @if (submitAttempted() && form.get('clientVatNumber')?.invalid) {
                            <div class="tw:mt-1 tw:text-sm tw:text-error-500">{{ clientVatInvalidMessage() }}</div>
                        }
                    </div>
                    <div class="tw:w-1/2">
                        <div class="tw:font-semibold">{{ 'INVOICE_PAYMENT_METHOD.name' | translate }}</div>
                        <div class="tw:text-neutral-500">{{ form.value.clientName || '-' }}</div>
                    </div>
                }
            </div>
            <!-- CAE is the merchant's own activity code, so it stays editable regardless of clientEditMode. -->
            @if (showCaeSelection() && caeOptions().length) {
                <div class="tw:mt-3">
                    <app-dropdown-input-v3
                        formControlName="cae"
                        label="{{ 'INVOICE_PAYMENT_METHOD.cae' | translate }}"
                        displayProperty="label"
                        [options]="caeOptions()"
                        [submitAttempted]="submitAttempted()"
                        [hideError]="true"
                    ></app-dropdown-input-v3>
                </div>
            }
        </div>

        @if (hasPaymentAmountToCollect()) {
            <div class="tw:mb-6">
                <app-tab-bar [(activeTab)]="activeTab" [tabOptions]="tabOptions" (tabChanged)="onPaymentTabSelected($event)"></app-tab-bar>
                <div class="tw:mt-4 tw:text-neutral-500">
                    @if (activeTab() === 'later') {
                        {{ 'INVOICE_PAYMENT_METHOD.payLaterDescription' | translate }}
                    } @else if (activeTab() === 'partial') {
                        {{ 'INVOICE_PAYMENT_METHOD.partialPayDescription' | translate }}
                    } @else if (activeTab() === 'full') {
                        {{ 'INVOICE_PAYMENT_METHOD.fullPayDescription' | translate }}
                    }
                </div>
            </div>
        }

        @if (hasPaymentAmountToCollect() && activeTab() === 'later') {
            <div class="tw:mb-6 tw:rounded-lg tw:border tw:border-primary-100 tw:bg-primary-50 tw:p-4">
                <div class="tw:mb-2 tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.payLaterTitle' | translate }}</div>
                <ul class="tw:list-inside tw:list-disc tw:text-sm">
                    <li>{{ 'INVOICE_PAYMENT_METHOD.payLaterList1' | translate }}</li>
                    <li>{{ 'INVOICE_PAYMENT_METHOD.payLaterList2' | translate }}</li>
                    <!-- <li>{{ "INVOICE_PAYMENT_METHOD.payLaterList3" | translate }}</li> -->
                </ul>
            </div>
        } @else if (hasPaymentAmountToCollect() && activeTab() === 'partial') {
            <div class="tw:mb-6 tw:rounded-lg tw:bg-primary-50 tw:p-4">
                <div class="tw:mb-2 tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.payNow' | translate }}</div>
                <div class="tw:mb-4 tw:flex tw:items-center tw:gap-4">
                    <div class="tw:w-28">
                        <!-- [min]="0" -->
                        <app-text-input
                            formControlName="partialPaymentAmount"
                            [hasMarginB]="false"
                            [submitAttempted]="submitAttempted()"
                            inputType="number"
                            suffix="{{ currentMerchant()?.config?.currency || null }}"
                            placeholder="0.00"
                        />
                    </div>
                    <div class="tw:font-semibold tw:text-neutral-500">
                        {{ 'GENERAL.of' | translate }} {{ maxPaymentAmountCents() / 100 | merchantCurrency }}
                    </div>
                </div>
                <div class="tw:text-neutral-500">
                    {{ 'INVOICE_PAYMENT_METHOD.partialPaymentMissing' | translate }}
                    <span class="tw:font-semibold">{{
                        maxPaymentAmountCents() / 100 - form.value.partialPaymentAmount || 0 | merchantCurrency
                    }}</span>
                </div>
            </div>
        }

        @if (hasPaymentAmountToCollect() && ['full', 'partial'].includes(activeTab())) {
            <div class="tw:mb-6" #paymentMethodsContainer>
                <div class="tw:mb-3 tw:font-semibold tw:text-primary-500">{{ 'INVOICE_PAYMENT_METHOD.paymentMethod' | translate }}</div>

                <div
                    class="tw:mb-4 tw:grid tw:gap-2.5"
                    [class.tw:grid-cols-2]="paymentChannelTabs().length <= 2"
                    [class.tw:grid-cols-3]="paymentChannelTabs().length === 3"
                    [class.tw:grid-cols-4]="paymentChannelTabs().length >= 4"
                    role="tablist"
                    [attr.aria-label]="'INVOICE_PAYMENT_METHOD.paymentMethod' | translate"
                >
                    @for (tab of paymentChannelTabs(); track tab.id) {
                        <button
                            type="button"
                            role="tab"
                            (click)="selectPaymentChannel(tab.id)"
                            class="tw:flex tw:min-w-0 tw:flex-col tw:items-center tw:gap-1.5 tw:!rounded-2xl tw:border tw:border-solid tw:px-2 tw:py-3 tw:text-center tw:transition-all tw:appearance-none"
                            [ngClass]="
                                paymentChannel() === tab.id
                                    ? 'tw:!border-primary-600 tw:!bg-primary-600 tw:text-white tw:shadow-md tw:shadow-primary-600/20'
                                    : 'tw:!border-neutral-150 tw:!bg-white tw:text-neutral-800 tw:hover:!border-neutral-300 tw:hover:!bg-neutral-50'
                            "
                            [attr.aria-selected]="paymentChannel() === tab.id"
                        >
                            <span
                                class="tw:flex tw:h-10 tw:w-10 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:!rounded-xl tw:transition-colors"
                                [ngClass]="choiceTabIconWellClass(tab, paymentChannel() === tab.id)"
                            >
                                @if (tab.logoSrc) {
                                    <img [src]="tab.logoSrc" alt="" [class]="tab.logoClass || 'tw:h-8 tw:w-8 tw:object-contain'" />
                                } @else {
                                    <app-icon
                                        [icon]="tab.icon"
                                        [extraClass]="
                                            paymentChannel() === tab.id
                                                ? 'tw:!text-xl tw:!leading-none tw:!text-white'
                                                : 'tw:!text-xl tw:!leading-none tw:!text-primary-600'
                                        "
                                    />
                                }
                            </span>
                            <span class="tw:w-full tw:truncate tw:text-sm tw:font-semibold tw:leading-tight">{{
                                tab.labelKey | translate
                            }}</span>
                            <span
                                class="tw:w-full tw:truncate tw:text-[11px] tw:font-medium tw:leading-tight"
                                [ngClass]="paymentChannel() === tab.id ? 'tw:text-white/75' : 'tw:text-neutral-400'"
                                >{{ tab.hintKey | translate }}</span
                            >
                        </button>
                    }
                </div>

                @if (paymentChannel() === 'register') {
                    <app-multi-check-buttons
                        errorMessage="{{ 'INVOICE_PAYMENT_METHOD.paymentMethodRequired' | translate }}"
                        [submitAttempted]="submitAttempted()"
                        formControlName="activePaymentMethods"
                        [selectOptions]="paymentMethods()"
                    ></app-multi-check-buttons>

                    @if (selectedRegisterMethods().length > 1) {
                        <div
                            class="tw:mt-4 tw:rounded-lg tw:bg-primary-50 tw:p-4"
                            [class.tw:!bg-error-100]="submitAttempted() && form.get('salePayments')?.errors"
                        >
                            <div class="tw:mb-2 tw:font-semibold tw:text-primary-500">
                                {{ 'INVOICE_PAYMENT_METHOD.valueByPaymentMethod' | translate }}
                            </div>
                            <div class="tw:flex tw:flex-col tw:justify-start tw:gap-4" formGroupName="salePayments">
                                @for (line of paymentMethods(); let i = $index; track i) {
                                    @if (selectedRegisterMethods().includes(line.value)) {
                                        <div class="tw:flex tw:items-center tw:justify-between">
                                            <div class="tw:font-semibold tw:text-neutral-500">{{ line.label }}</div>
                                            <div class="tw:w-28">
                                                <app-text-input
                                                    [hasMarginB]="false"
                                                    formControlName="{{ line.value }}"
                                                    [submitAttempted]="submitAttempted()"
                                                    inputType="number"
                                                    suffix="{{ currentMerchant()?.config?.currency || null }}"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        </div>
                                    }
                                }
                            </div>
                            @if (form.get('salePayments')?.errors && submitAttempted()) {
                                <div class="tw:mt-2 tw:text-sm tw:text-error-500">
                                    {{ 'INVOICE_PAYMENT_METHOD.paymentSumError' | translate }}
                                </div>
                            }
                        </div>
                    }
                } @else if (paymentChannel() === 'mbway_request') {
                    @if (isChannelActive('mbway_request')) {
                        <div class="tw:mt-2 tw:space-y-3 tw:!rounded-xl tw:border tw:border-neutral-200 tw:bg-neutral-50 tw:p-4">
                            <div>
                                <label class="tw:mb-1.5 tw:block tw:text-sm tw:font-medium tw:text-neutral-700">{{
                                    'INVOICE_PAYMENT_METHOD.phoneLabel' | translate
                                }}</label>
                                <app-phone-input
                                    [form]="form"
                                    controlName="requestPhone"
                                    [submitAttempted]="submitAttempted()"
                                ></app-phone-input>
                            </div>
                            <p class="tw:mb-0 tw:text-xs tw:text-neutral-400">
                                {{ 'INVOICE_PAYMENT_METHOD.mbWayHelper' | translate }}
                            </p>
                        </div>
                    } @else {
                        <div class="tw:!rounded-xl tw:border tw:border-warning-200 tw:bg-warning-50 tw:px-4 tw:py-3">
                            <div class="tw:flex tw:items-start tw:gap-2">
                                <app-icon icon="warning" extraClass="tw:!text-warning-500" />
                                <div>
                                    <div class="tw:text-sm tw:font-medium tw:text-neutral-800">
                                        {{ 'INVOICE_PAYMENT_METHOD.methodNotActive' | translate }}
                                    </div>
                                    <p class="tw:mb-0 tw:mt-1 tw:text-xs tw:text-neutral-500">
                                        {{ 'INVOICE_PAYMENT_METHOD.activateMbWayHint' | translate }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    }
                } @else if (paymentChannel() === 'bukpay') {
                    @if (isChannelActive('bukpay')) {
                        <div class="tw:mt-2 tw:space-y-3 tw:!rounded-xl tw:border tw:border-neutral-200 tw:bg-neutral-50 tw:p-4">
                            <div>
                                <label class="tw:mb-1.5 tw:block tw:text-sm tw:font-medium tw:text-neutral-700">{{
                                    'INVOICE_PAYMENT_METHOD.emailLabel' | translate
                                }}</label>
                                <app-text-input
                                    [submitAttempted]="submitAttempted()"
                                    [hasMarginB]="false"
                                    inputType="email"
                                    formControlName="requestEmail"
                                    placeholder="{{ 'INVOICE_PAYMENT_METHOD.emailPlaceholder' | translate }}"
                                    invalidMsg="{{ 'GENERAL.invalidEmail' | translate }}"
                                ></app-text-input>
                            </div>
                            <p class="tw:mb-0 tw:text-xs tw:text-neutral-400">
                                {{ 'INVOICE_PAYMENT_METHOD.bukPayHelper' | translate }}
                            </p>
                        </div>
                    } @else {
                        <div class="tw:!rounded-xl tw:border tw:border-warning-200 tw:bg-warning-50 tw:px-4 tw:py-3">
                            <div class="tw:flex tw:items-start tw:gap-2">
                                <app-icon icon="warning" extraClass="tw:!text-warning-500" />
                                <div>
                                    <div class="tw:text-sm tw:font-medium tw:text-neutral-800">
                                        {{ 'INVOICE_PAYMENT_METHOD.methodNotActive' | translate }}
                                    </div>
                                    <p class="tw:mb-0 tw:mt-1 tw:text-xs tw:text-neutral-500">
                                        {{ 'INVOICE_PAYMENT_METHOD.activateBukPayHint' | translate }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    }
                } @else if (paymentChannel() === 'teya') {
                    @if (isChannelActive('teya')) {
                        <p class="tw:mb-0 tw:text-sm tw:text-neutral-500">
                            {{ 'INVOICE_PAYMENT_METHOD.teyaTerminalHint' | translate }}
                        </p>
                    } @else {
                        <div class="tw:!rounded-xl tw:border tw:border-warning-200 tw:bg-warning-50 tw:px-4 tw:py-3">
                            <div class="tw:flex tw:items-start tw:gap-2">
                                <app-icon icon="warning" extraClass="tw:!text-warning-500" />
                                <div>
                                    <div class="tw:text-sm tw:font-medium tw:text-neutral-800">
                                        {{ 'INVOICE_PAYMENT_METHOD.methodNotActive' | translate }}
                                    </div>
                                    <p class="tw:mb-0 tw:mt-1 tw:text-xs tw:text-neutral-500">
                                        {{ 'INVOICE_PAYMENT_METHOD.activateTeyaHint' | translate }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    }
                }

                @if (showChannelAmountSummary()) {
                    <div class="tw:mt-3 tw:flex tw:items-center tw:justify-between tw:!rounded-xl tw:bg-neutral-50 tw:px-4 tw:py-3">
                        <div>
                            <div class="tw:text-xs tw:font-medium tw:text-neutral-400">{{ 'INVOICE_PAYMENT_METHOD.value' | translate }}</div>
                            <div class="tw:text-sm tw:font-medium tw:text-neutral-800">{{ channelAmountLabel() }}</div>
                        </div>
                        <span class="tw:text-base tw:font-semibold tw:text-neutral-900">{{ amountDueCents() / 100 | merchantCurrency }}</span>
                    </div>
                }
            </div>
        }

        @if (checkoutMode() === CheckoutMode.INVOICE_REMAINING && existingSaleData()?.invoiceStatus === InvoiceStatus.PARTIALLY_INVOICED) {
            <div class="tw:flex tw:mb-2 tw:items-center tw:justify-start tw:gap-4 tw:px-4 tw:py-2 tw:text-sm tw:bg-primary-100 tw:rounded-lg">
                <app-icon icon="info" class="tw:text-primary-400"></app-icon>
                {{ 'INVOICE_PREVIEW.cancelOldInvoiceWarning' | translate }}
            </div>
        }

        @if (checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
            <app-textarea-input
                [rows]="2"
                formControlName="description"
                invalidMsg="{{ 'INVOICE_PAYMENT_METHOD.descriptionError' | translate }}"
                label="{{ 'INVOICE_PAYMENT_METHOD.descriptionLabel' | translate }}"
                [submitAttempted]="submitAttempted()"
                placeholder="{{ 'INVOICE_PAYMENT_METHOD.descriptionPlaceholder' | translate }}"
            />
        }
        <!-- Matches buk-ds NewSaleModal footer (native Button size=lg; flex-1 on wrapper because app-button host isn't a flex item). -->
        <div class="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-10 tw:flex tw:shrink-0 tw:items-center tw:gap-3 tw:border-t tw:border-neutral-150 tw:bg-white tw:px-6 tw:py-4 tw:sm:px-10 tw:lg:left-1/2">
            @if (checkoutMode() === CheckoutMode.CREATE_FULL_SALE) {
                <app-button
                    type="secondary"
                    size="lg"
                    [disabled]="saving()"
                    extraClass="tw:shrink-0 tw:!font-medium"
                    (clickEvent)="goBack()"
                >
                    {{ 'GENERAL.back' | translate }}
                </app-button>
                <app-button
                    type="secondary"
                    size="lg"
                    [disabled]="saving()"
                    extraClass="tw:shrink-0 tw:!font-medium"
                    (clickEvent)="saveDraft()"
                >
                    {{ 'GENERAL.saveDraft' | translate }}
                </app-button>
                <div class="tw:min-w-0 tw:flex-1">
                    <app-button
                        buttonType="submit"
                        type="primary"
                        size="lg"
                        [loading]="saving()"
                        [disabled]="saving()"
                        extraClass="tw:w-full tw:!font-medium"
                    >
                        {{ submitButtonCopy() | translate }}
                    </app-button>
                </div>
            } @else if (checkoutMode() === CheckoutMode.INVOICE_REMAINING) {
                <div class="tw:w-full tw:flex-1">
                    <app-button
                        buttonType="submit"
                        type="primary"
                        size="lg"
                        [loading]="saving()"
                        [disabled]="saving()"
                        extraClass="tw:w-full tw:!font-medium"
                    >
                        {{ submitButtonCopy() | translate }}
                    </app-button>
                </div>
            } @else {
                <div class="tw:flex tw:w-full tw:flex-1 tw:gap-3">
                    @if (existingSaleData()?.status === ISaleStatus.DRAFT) {
                        <app-button
                            type="secondary"
                            size="lg"
                            [disabled]="saving()"
                            extraClass="tw:shrink-0 tw:!font-medium"
                            (clickEvent)="updateDraft()"
                        >
                            {{ 'GENERAL.updateDraft' | translate }}
                        </app-button>
                    }
                    <div class="tw:min-w-0 tw:flex-1">
                        <app-button
                            buttonType="submit"
                            type="primary"
                            size="lg"
                            [loading]="saving()"
                            [disabled]="saving()"
                            extraClass="tw:w-full tw:!font-medium"
                        >
                            {{ submitButtonCopy() | translate }}
                        </app-button>
                    </div>
                </div>
            }
        </div>
    </form>
}
